import { computed } from 'vue';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';
import promotionsConfig from '@/features/promotions/config.json';
import { MaybeRefOrGetter, toValue } from 'vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { getPrimeAssociatesSuffix } from '@/helpers/promotion-helper';
import { NATIVE_AD_PROVIDERS, type NativeAdSlotKey } from '@/features/native-ads/helpers/native-ad-config';

export function useNativeAds(props: { title: MaybeRefOrGetter<TitleDetail | undefined>; slot?: NativeAdSlotKey }) {
	const { country } = storeToRefs(useLanguageStore());
	const { isProUser } = storeToRefs(useUserStore());

	const appleProgrammaticCountries = computed(() => promotionsConfig.gam?.apple_programmatic ?? ([] as string[]));
	const mxxProgrammaticCountries = computed(() => promotionsConfig.gam?.mxx_programmatic ?? ([] as string[]));
	const mxxListProgrammaticCountries = computed(
		() => promotionsConfig.gam?.mxx_programmatic_list ?? ([] as string[])
	);
	const title = computed(() => toValue(props.title));

	const isAppleTitle = computed(() => !!title.value?.offers.some(offer => offer.package.shortName === 'atp'));

	const nativeAdsShortName = computed((): string | null => {
		if (isProUser.value) return null;
		if (appleProgrammaticCountries.value.includes(country.value)) return 'atp';
		if (mxxProgrammaticCountries.value.includes(country.value)) return isAppleTitle.value ? null : 'mxx';
		if (
			mxxListProgrammaticCountries.value.includes(country.value) &&
			title.value?.offers.some(offer => offer.package.shortName === 'mxx')
		)
			return 'mxx';
		return null;
	});

	const activeSlots = computed((): NativeAdSlotKey[] => {
		if (!nativeAdsShortName.value || !NATIVE_AD_PROVIDERS[nativeAdsShortName.value]) return [];

		const titleHasActiveProviderOffer = title.value?.offers?.some(
			offer => offer.package.shortName === nativeAdsShortName.value
		);

		return titleHasActiveProviderOffer
			? NATIVE_AD_PROVIDERS[nativeAdsShortName.value].titlesWithOffer
			: NATIVE_AD_PROVIDERS[nativeAdsShortName.value].fallbackTitles;
	});

	const shouldShowNativeAd = computed(() => {
		if (!props.slot) return false;
		return activeSlots.value.includes(props.slot);
	});

	function setSQTVFallbackTag(clickoutUrl: ClickoutUrl, shortName: string) {
		if (!nativeAdsShortName.value || nativeAdsShortName.value === 'mxx') return clickoutUrl;
		let tag = 'native-sqtv-fb';

		if (['amp', 'prv'].includes(shortName)) {
			const primeSuffix = getPrimeAssociatesSuffix(country.value);
			tag = primeSuffix ? `${tag}-${primeSuffix}` : tag;
		}

		return clickoutUrl.set('uct_ctag', tag);
	}

	return {
		nativeAdsShortName,
		activeSlots,
		shouldShowNativeAd,
		setSQTVFallbackTag,
	};
}
